import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Loader2, TrendingUp, FileText, Clock, Target } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import ReactMarkdown from "react-markdown";

const ASSETS = ["BTC", "ETH", "SOL", "AVAX", "BNB", "MATIC", "ARB", "OP"];

export function AIAnalystDashboard() {
  const [loading, setLoading] = useState<string | null>(null);
  const [showAssetModal, setShowAssetModal] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState("");
  const [analysisResult, setAnalysisResult] = useState<string | null>(null);
  const [showResultModal, setShowResultModal] = useState(false);

  // Fetch stats
  const { data: stats } = useQuery({
    queryKey: ["ai-analyst-stats"],
    queryFn: async () => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const [lastDaily, lastWeekly, todayExec] = await Promise.all([
        supabase
          .from("alpha_onchain_reports")
          .select("created_at")
          .eq("report_type", "daily")
          .order("created_at", { ascending: false })
          .limit(1)
          .maybeSingle(),
        supabase
          .from("alpha_onchain_reports")
          .select("created_at")
          .eq("report_type", "weekly")
          .order("created_at", { ascending: false })
          .limit(1)
          .maybeSingle(),
        supabase
          .from("alpha_onchain_executions")
          .select("success")
          .gte("executed_at", today.toISOString()),
      ]);

      const totalToday = todayExec.data?.length || 0;
      const successToday = todayExec.data?.filter((e) => e.success).length || 0;
      const successRate = totalToday > 0 ? (successToday / totalToday) * 100 : 0;

      return {
        lastDaily: lastDaily.data?.created_at,
        lastWeekly: lastWeekly.data?.created_at,
        totalToday,
        successRate,
      };
    },
    refetchInterval: 30000,
  });

  const triggerReport = async (reportType: "daily" | "weekly") => {
    setLoading(reportType);
    try {
      const { data, error } = await supabase.functions.invoke("ai-analyst-brain", {
        body: { 
          endpoint: "generate-report",
          reportType 
        },
      });

      if (error) throw error;

      toast.success(`Relatório ${reportType === "daily" ? "diário" : "semanal"} gerado com sucesso!`);
      setAnalysisResult(data.report);
      setShowResultModal(true);
    } catch (error: any) {
      console.error("Error triggering report:", error);
      toast.error(`Erro ao gerar relatório: ${error.message}`);
    } finally {
      setLoading(null);
    }
  };

  const analyzeAsset = async () => {
    if (!selectedAsset) {
      toast.error("Selecione um ativo");
      return;
    }

    setLoading("asset");
    setShowAssetModal(false);
    try {
      const { data, error } = await supabase.functions.invoke("ai-analyst-brain", {
        body: {
          endpoint: "analyze-opportunity",
          asset: selectedAsset,
          alertType: "Manual Analysis",
        },
      });

      if (error) throw error;

      toast.success(`Análise de ${selectedAsset} concluída!`);
      setAnalysisResult(data.analysis);
      setShowResultModal(true);
    } catch (error: any) {
      console.error("Error analyzing asset:", error);
      toast.error(`Erro ao analisar ativo: ${error.message}`);
    } finally {
      setLoading(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Status Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Último Relatório Diário</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {stats?.lastDaily ? new Date(stats.lastDaily).toLocaleDateString("pt-BR") : "N/A"}
            </div>
            <p className="text-xs text-muted-foreground">
              {stats?.lastDaily
                ? `${Math.round((Date.now() - new Date(stats.lastDaily).getTime()) / 3600000)}h atrás`
                : "Nenhum relatório"}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Último Relatório Semanal</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {stats?.lastWeekly ? new Date(stats.lastWeekly).toLocaleDateString("pt-BR") : "N/A"}
            </div>
            <p className="text-xs text-muted-foreground">
              {stats?.lastWeekly
                ? `${Math.round((Date.now() - new Date(stats.lastWeekly).getTime()) / 86400000)} dias atrás`
                : "Nenhum relatório"}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Análises Hoje</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.totalToday || 0}</div>
            <p className="text-xs text-muted-foreground">Total de execuções</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Taxa de Sucesso</CardTitle>
            <Target className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.successRate.toFixed(1)}%</div>
            <p className="text-xs text-muted-foreground">Últimas 24h</p>
          </CardContent>
        </Card>
      </div>

      {/* Action Buttons */}
      <Card>
        <CardHeader>
          <CardTitle>Ações Rápidas</CardTitle>
          <CardDescription>Disparar análises e relatórios manualmente</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-4">
          <Button
            onClick={() => triggerReport("daily")}
            disabled={loading === "daily"}
            className="flex items-center gap-2"
          >
            {loading === "daily" ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <FileText className="h-4 w-4" />
            )}
            Gerar Relatório Diário
          </Button>

          <Button
            onClick={() => triggerReport("weekly")}
            disabled={loading === "weekly"}
            variant="outline"
            className="flex items-center gap-2"
          >
            {loading === "weekly" ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <FileText className="h-4 w-4" />
            )}
            Gerar Relatório Semanal
          </Button>

          <Button
            onClick={() => setShowAssetModal(true)}
            disabled={loading === "asset"}
            variant="secondary"
            className="flex items-center gap-2"
          >
            {loading === "asset" ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <TrendingUp className="h-4 w-4" />
            )}
            Analisar Ativo
          </Button>
        </CardContent>
      </Card>

      {/* Asset Selection Modal */}
      <Dialog open={showAssetModal} onOpenChange={setShowAssetModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Selecionar Ativo para Análise</DialogTitle>
            <DialogDescription>Escolha o ativo que deseja analisar</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Select value={selectedAsset} onValueChange={setSelectedAsset}>
              <SelectTrigger>
                <SelectValue placeholder="Selecione um ativo" />
              </SelectTrigger>
              <SelectContent>
                {ASSETS.map((asset) => (
                  <SelectItem key={asset} value={asset}>
                    {asset}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button onClick={analyzeAsset} className="w-full" disabled={!selectedAsset}>
              Analisar {selectedAsset}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Result Modal */}
      <Dialog open={showResultModal} onOpenChange={setShowResultModal}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Resultado da Análise</DialogTitle>
          </DialogHeader>
          <div className="prose prose-sm dark:prose-invert max-w-none">
            <ReactMarkdown>{analysisResult || ""}</ReactMarkdown>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
